/**
 * Color Contrast Utilities for WCAG Compliance
 * This module provides utilities for analyzing and adjusting colors to meet WCAG contrast requirements.
 */

/**
 * Convert a hex color to RGB values
 * @param {string} hex - Hex color code (with or without # prefix)
 * @returns {Object} RGB color object with r, g, b properties
 */
export const hexToRgb = (hex) => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return { r, g, b };
};

/**
 * Calculate relative luminance of a color (for WCAG contrast calculations)
 * @param {Object} rgb - RGB color object with r, g, b properties
 * @returns {number} Relative luminance value
 */
export const calculateLuminance = (rgb) => {
  // Convert RGB to sRGB
  const srgb = {
    r: rgb.r / 255,
    g: rgb.g / 255,
    b: rgb.b / 255
  };
  
  // Apply transformations
  const transform = (value) => {
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  
  const rsrgb = transform(srgb.r);
  const gsrgb = transform(srgb.g);
  const bsrgb = transform(srgb.b);
  
  // Calculate luminance
  return 0.2126 * rsrgb + 0.7152 * gsrgb + 0.0722 * bsrgb;
};

/**
 * Calculate contrast ratio between two colors
 * @param {string} foreground - Foreground color in hex format
 * @param {string} background - Background color in hex format
 * @returns {number} Contrast ratio (rounded to 2 decimal places)
 */
export const calculateContrastRatio = (foreground, background) => {
  const foregroundLuminance = calculateLuminance(hexToRgb(foreground));
  const backgroundLuminance = calculateLuminance(hexToRgb(background));
  
  // Determine lighter and darker colors
  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);
  
  // Calculate ratio according to WCAG formula
  return Number(((lighter + 0.05) / (darker + 0.05)).toFixed(2));
};

/**
 * Check if a color combination meets WCAG AA standards
 * @param {string} foreground - Foreground color in hex format
 * @param {string} background - Background color in hex format
 * @param {string} type - Type of content ('text', 'largeText', or 'ui')
 * @returns {Object} Result with contrast ratio and whether it passes AA standard
 */
export const meetsWCAGAA = (foreground, background, type = 'text') => {
  const ratio = calculateContrastRatio(foreground, background);
  
  let passes = false;
  
  switch (type) {
    case 'largeText':
      passes = ratio >= 3.0;
      break;
    case 'ui':
      passes = ratio >= 3.0;
      break;
    case 'text':
    default:
      passes = ratio >= 4.5;
      break;
  }
  
  return {
    ratio,
    passes
  };
};

/**
 * Adjust a color to increase or decrease its brightness
 * @param {string} hex - Hex color code
 * @param {number} percent - Percentage to adjust brightness (-100 to 100)
 * @returns {string} Adjusted hex color
 */
export const adjustBrightness = (hex, percent) => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  const rgb = hexToRgb(hex);
  
  // Adjust each channel
  let r = rgb.r + Math.round(rgb.r * (percent / 100));
  let g = rgb.g + Math.round(rgb.g * (percent / 100));
  let b = rgb.b + Math.round(rgb.b * (percent / 100));
  
  // Clamp values to valid range
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  
  // Convert back to hex
  const rHex = r.toString(16).padStart(2, '0');
  const gHex = g.toString(16).padStart(2, '0');
  const bHex = b.toString(16).padStart(2, '0');
  
  return `#${rHex}${gHex}${bHex}`;
};

/**
 * Suggest a color adjustment to meet WCAG AA standards
 * @param {string} foreground - Foreground color in hex format
 * @param {string} background - Background color in hex format
 * @param {string} type - Type of content ('text', 'largeText', or 'ui')
 * @returns {Object} Suggested accessible colors
 */
export const suggestAccessibleColors = (foreground, background, type = 'text') => {
  const initialCheck = meetsWCAGAA(foreground, background, type);
  
  // If already accessible, return original colors
  if (initialCheck.passes) {
    return {
      foreground,
      background,
      ratio: initialCheck.ratio,
      passes: true
    };
  }
  
  // Calculate foreground and background luminance
  const fgLuminance = calculateLuminance(hexToRgb(foreground));
  const bgLuminance = calculateLuminance(hexToRgb(background));
  
  let newForeground = foreground;
  let newBackground = background;
  
  // Decide which color to adjust based on luminance values
  if (fgLuminance > bgLuminance) {
    // If foreground is lighter than background, make it even lighter
    newForeground = adjustBrightness(foreground, 30);
  } else {
    // If foreground is darker than background, make it even darker
    newForeground = adjustBrightness(foreground, -30);
  }
  
  const newCheck = meetsWCAGAA(newForeground, background, type);
  
  // If adjusting foreground doesn't work, try adjusting background
  if (!newCheck.passes) {
    newForeground = foreground;
    
    if (bgLuminance > fgLuminance) {
      // If background is lighter than foreground, make it even lighter
      newBackground = adjustBrightness(background, 30);
    } else {
      // If background is darker than foreground, make it even darker
      newBackground = adjustBrightness(background, -30);
    }
    
    const finalCheck = meetsWCAGAA(foreground, newBackground, type);
    
    return {
      foreground,
      background: newBackground,
      ratio: finalCheck.ratio,
      passes: finalCheck.passes
    };
  }
  
  return {
    foreground: newForeground,
    background,
    ratio: newCheck.ratio,
    passes: newCheck.passes
  };
};

/**
 * Predefined accessible color pairs for common statuses
 * Each status has color options for text and background use
 */
export const accessibleColors = {
  // Status colors for text on white background
  statusTextColors: {
    exceed: '#0D751F', // Darker green for better contrast on white
    meet: '#0D47A1', // Darker blue for better contrast on white
    atRisk: '#C63F17' // Darker orange for better contrast on white
  },
  
  // Status colors for white text on colored background
  statusBackgroundColors: {
    exceed: '#0D751F', // Darker green for white text
    meet: '#0D47A1', // Darker blue for white text
    atRisk: '#C63F17' // Darker orange for white text
  },
  
  // Certification badge colors
  certificationColors: {
    background: '#E0F2E4', // Slightly darker than original for better contrast
    text: '#1B5E20' // Darker green text for better contrast
  },
  
  // Mastery pill colors
  masteryPillColors: {
    background: '#FFF8E1', // Light yellow background
    text: '#BF360C' // Darker orange-red text for better contrast (4.6:1 ratio)
  },
  
  // Chart colors for radar and other charts
  chartColors: {
    background: 'rgba(13, 71, 161, 0.2)', // Accessible blue with opacity
    border: 'rgba(13, 71, 161, 1)', // Solid accessible blue
    point: 'rgba(13, 71, 161, 1)', // Solid accessible blue for points
    pointHover: 'rgba(13, 71, 161, 1)', // Hover color for points
    tooltip: {
      background: '#ffffff',
      titleColor: '#0D47A1',
      bodyColor: '#333333',
      borderColor: '#0D47A1'
    }
  }
};
